/* eslint-disable max-lines-per-function */
import prepareComponent from "@bluframe/grapple/prepareComponent"
import {
  TechSpecIdentifier,
  TechSpecsSelectorProps
} from "@bluframe/mightychroma-mightytool-frontend-types"
import { memo, useCallback, useMemo, useState } from "react"

import Selector from "./Selector"

export interface Props extends TechSpecsSelectorProps {}

export interface ComponentProps extends Props {
  onCancel: () => void
  onConfirm: () => void
  onSelectAllTechSpecs: (techSpecs: TechSpecIdentifier[][]) => () => void
  onSelectTechSpec: (techSpec: TechSpecIdentifier[]) => () => void
}

export const usePrepareComponent = ({
  modal,
  onSelectClick: onSelectClickProp,
  onSetTechSpecs,
  techSpecsSelected: techSpecsSelectedProp,
  ...props
}: Props): ComponentProps => {
  const [techSpecsSelectedDraft, setTechSpecsSelectedDraft] = useState<
    TechSpecIdentifier[][]
  >(() => techSpecsSelectedProp)
  const techSpecsSelected = useMemo(
    () => (modal.isOpen ? techSpecsSelectedDraft : techSpecsSelectedProp),
    [modal.isOpen, techSpecsSelectedDraft, techSpecsSelectedProp]
  )

  const onSelectClick = useCallback(() => {
    setTechSpecsSelectedDraft(() => techSpecsSelectedProp)
    onSelectClickProp()
  }, [onSelectClickProp, techSpecsSelectedProp])

  /**
   *  onConfirm
   *
   *  @description Confirm the selected tech specs
   *  @param void
   *  @returns void
   *
   *  @example onConfirm()
   *
   */
  const onConfirm = useCallback(() => {
    onSetTechSpecs(techSpecsSelectedDraft)

    modal.onClose()
  }, [modal, onSetTechSpecs, techSpecsSelectedDraft])

  /**
   *  onSelectTechSpec
   *
   *  @description Add or remove tech spec from the selected list
   *  @param techSpec
   *  @returns void
   *
   *  @example onSelectTechSpec(["4K", "HDR"])
   *
   */
  const onSelectTechSpec = useCallback(
    (techSpec: TechSpecIdentifier[]) => () => {
      // Add and remove tech specs
      setTechSpecsSelectedDraft((prevState) => {
        const newState = [...prevState]

        // Find the index of the existing array if it exists
        const index = prevState.findIndex(
          (item) =>
            item.length === techSpec.length &&
            item.every((spec, specIndex) => spec === techSpec[specIndex])
        )

        if (index < 0) {
          // If it doesn't exist, add it
          newState.push(techSpec)
        } else {
          // If it exists, remove it
          newState.splice(index, 1)
        }
        // Return new state
        return newState
      })
    },
    [setTechSpecsSelectedDraft]
  )

  /**
   * onSelectAllTechSpecs
   *
   * @description Select all tech specs
   * @param techSpecs
   * @returns void
   *
   * @example onSelectAllTechSpecs([["4K"], ["HDR"]])
   *
   */
  const onSelectAllTechSpecs = useCallback(
    (techSpecs: TechSpecIdentifier[][]) => () => {
      setTechSpecsSelectedDraft(() => techSpecs)
    },
    [setTechSpecsSelectedDraft]
  )

  /**
   * onCancel
   *
   * @description Cancel the modal
   * @param void
   * @returns void
   *
   * @example onCancel()
   *
   */
  const onCancel = useCallback(() => {
    setTechSpecsSelectedDraft(() => techSpecsSelectedProp)

    modal.onClose()
  }, [modal, techSpecsSelectedProp])

  return {
    ...props,
    modal,
    onCancel,
    onConfirm,
    onSetTechSpecs,
    onSelectAllTechSpecs,
    onSelectClick,
    onSelectTechSpec,
    techSpecsSelected
  }
}

export default prepareComponent<Props, ComponentProps>(usePrepareComponent)(
  memo(Selector)
)
